import { createSelector } from 'reselect';
import { IMobileAppRootState } from '../../mobileAppCore/types/mobileAppState.t';
import { getIsAppVisibility, getUserAccounts, getUserSelectedAccountCode } from '../../app/appSelectors';
import _ from 'lodash';
import { getLiteTradeConfig, getLiteTradeSessionToken, getMarketDataCandleWatchers, getMarketDataWatchers } from '../../modules/LiteTrade/liteTradeSelectors';

const moduleSelector = (state: IMobileAppRootState) => state.websocket;

export const getWebsocketChannels = (state) => {
    return moduleSelector(state).channels
};

export const getWebsocketReadyState = (channel?: string) => (state: IMobileAppRootState): { [channel: string]: WebSocket["readyState"] } | WebSocket["readyState"] => {
    const channels = getWebsocketChannels(state);
    if (channel) {
        return channels[channel]?.readyState;
    }
    return Object.keys(channels).reduce((acc, channel) => {
        acc[channel] = channels[channel].readyState;
        return acc;
    }, {});
};

export const getWebsocketReadyStateNum = (state: IMobileAppRootState) => {
    const readyStates = getWebsocketReadyState()(state);
    const readyState = (Object.values(readyStates) as number[]).reduce((acc, item) => {
        if (acc === null) return item;
        if (item === WebSocket.OPEN) return item;
        return acc;
    }, null);
    return readyState;
};

export const getWebsocketInited = (state: IMobileAppRootState) => moduleSelector(state)?.inited;

export const getWebsocketReadyStateConnecting = (state: IMobileAppRootState) => {
    const readyStates = getWebsocketReadyState()(state);
    const readyState = (Object.values(readyStates) as number[]).reduce((acc, item) => {
        if (acc === null) return item;
        if (item !== WebSocket.OPEN) return item;
        return acc;
    }, null);
    return readyState;
};

export const getWebsocketSubscriptionsId = createSelector(moduleSelector, (state) => state.subscriptionsById);

export const getWebsocketSubscriptionById = (channel: string, id: string) => (state: IMobileAppRootState) => {
    const subscriptionsById = getWebsocketSubscriptionsId(state);
    return subscriptionsById[channel] && subscriptionsById[channel][id];
};

export const initiationAccountEventsSelector = (state) => {
    const config = getLiteTradeConfig(state);
    const channel = config?.wssUrl;
    const selectedAccountCode = getUserSelectedAccountCode(state);
    const sessionToken = getLiteTradeSessionToken(state);
    const isAppVisibility = getIsAppVisibility(state);
    return selectedAccountCode && isAppVisibility && sessionToken && channel;
};

export const initiationLiteTradeAssetSelector = (state) => {
    const selectedAccountCode = getUserSelectedAccountCode(state);
    const sessionToken = getLiteTradeSessionToken(state);
    const isAppVisibility = getIsAppVisibility(state);
    return selectedAccountCode && isAppVisibility && sessionToken;
};

export const initiationAccountMetricsSelector = (state: IMobileAppRootState) => {
    const liteTradeConfig = getLiteTradeConfig(state);
    const channel = liteTradeConfig?.wssUrl;
    const isAppVisibility = getIsAppVisibility(state);
    const sessionToken = getLiteTradeSessionToken(state);
    const accounts = getUserAccounts(state);
    return channel && accounts.length && isAppVisibility && sessionToken;
};

export const initiationAccountPortfoliosSelector = (state: IMobileAppRootState) => {
    const liteTradeConfig = getLiteTradeConfig(state);
    const channel = liteTradeConfig?.wssUrl;
    const isAppVisibility = getIsAppVisibility(state);
    const sessionToken = getLiteTradeSessionToken(state);
    const accounts = getUserAccounts(state);
    return channel && accounts.length && isAppVisibility && sessionToken;
};

export const initiationConditionMarketDataQuoteSelector = (state: IMobileAppRootState) => {
    const liteTradeConfig = getLiteTradeConfig(state);
    const channel = liteTradeConfig?.wssMdUrl;
    const sessionToken = getLiteTradeSessionToken(state);
    const selectedAccountCode = getUserSelectedAccountCode(state);
    const marketDataWatchers = getMarketDataWatchers(state);
    const symbols = _.uniq(marketDataWatchers?.map((item) => item.symbol));
    return channel && sessionToken && selectedAccountCode && symbols && symbols.length;
};

export const initiationSymbolsDataWithCallbackSelector = (state: IMobileAppRootState) => {
    const liteTradeConfig = getLiteTradeConfig(state);
    const channel = liteTradeConfig?.wssMdUrl;
    const marketDataCandleWatchers = getMarketDataCandleWatchers(state);
    const sessionToken = getLiteTradeSessionToken(state);
    const selectedAccountCode = getUserSelectedAccountCode(state);

    return channel && marketDataCandleWatchers && sessionToken && selectedAccountCode;
};
